import React, { useState, useRef, useEffect } from 'react';
import { apiUrl } from '../../utils/apiBase';
import { MessageCircle, X, Send, Loader2, Maximize2, Minimize2, Settings, Database, Search, Tv, Bot } from 'lucide-react';

const ChatWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showSourcesPanel, setShowSourcesPanel] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: '🎾 Ciao! Sono il tuo assistente sportivo KainoTV. Abilita le sorgenti dati che preferisci usando il pulsante ⚙️ e poi chiedi informazioni su eventi di tennis, tornei, giocatori e programmazione TV!',
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sources, setSources] = useState({
    sportsdev: false,
    epg: false,
    brave_search: false,
    brave_ai: false
  });
  const [sourcesInfo, setSourcesInfo] = useState({
    sportsdev: { name: 'SportsDev API', description: 'Dati tennis professionali', icon: '🎾' },
    epg: { name: 'EPG TV Italia', description: 'Programmazione televisiva', icon: '📺' },
    brave_search: { name: 'Brave Search', description: 'Ricerca web aggiornata', icon: '🔍' },
    brave_ai: { name: 'Brave AI Search', description: 'Ricerca AI con riassunto', icon: '🤖' }
  });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Auto-fullscreen su mobile
  useEffect(() => {
    if (isOpen) {
      const isMobile = window.innerWidth < 768;
      setIsFullscreen(isMobile);
    }
  }, [isOpen]);

  // Carica lo stato delle sorgenti all'apertura
  useEffect(() => {
    if (isOpen) {
      loadSourcesStatus();
    }
  }, [isOpen]);

  const loadSourcesStatus = async () => {
    try {
      const response = await fetch(apiUrl('/api/sports-chat/sources'));
      if (response.ok) {
        const data = await response.json();
        setSources(data.sources || {});
        if (data.sourcesInfo) {
          setSourcesInfo(data.sourcesInfo);
        }
      }
    } catch (error) {
      console.error('Error loading sources status:', error);
    }
  };

  const toggleSource = async (sourceId) => {
    try {
      const newEnabled = !sources[sourceId];
      
      const response = await fetch(apiUrl('/api/sports-chat/sources/toggle'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sourceId, enabled: newEnabled })
      });

      if (response.ok) {
        setSources(prev => ({
          ...prev,
          [sourceId]: newEnabled
        }));

        // Mostra messaggio di conferma
        const statusMessage = {
          id: Date.now(),
          type: 'system',
          content: `${sourcesInfo[sourceId]?.icon || '📊'} ${sourcesInfo[sourceId]?.name || sourceId} ${newEnabled ? 'abilitata' : 'disabilitata'}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, statusMessage]);
      }
    } catch (error) {
      console.error('Error toggling source:', error);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await fetch(apiUrl('/api/sports-chat'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          message: userMessage.content,
          enabledSources: Object.keys(sources).filter(key => sources[key])
        })
      });

      const data = await response.json();

      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: data.response || 'Mi dispiace, c\'è stato un errore. Riprova più tardi.',
        timestamp: new Date(),
        sources: data.sourcesUsed || []
      };

      setMessages(prev => [...prev, botMessage]);
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: '❌ Errore di connessione. Riprova più tardi.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('it-IT', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsFullscreen(false);
    setShowSourcesPanel(false);
  };

  const getEnabledSourcesCount = () => {
    return Object.values(sources).filter(Boolean).length;
  };

  const renderSourcesPanel = () => (
    <div className="bg-gray-50 dark:bg-gray-700 border-t border-gray-200 dark:border-gray-600 p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">
          🔧 Sorgenti Dati
        </h4>
        <button
          onClick={() => setShowSourcesPanel(false)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
        >
          <X size={16} />
        </button>
      </div>
      
      <div className="space-y-2">
        {Object.entries(sourcesInfo).map(([sourceId, info]) => (
          <div key={sourceId} className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded-lg border border-gray-200 dark:border-gray-500">
            <div className="flex items-center space-x-2">
              <span className="text-lg">{info.icon}</span>
              <div>
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{info.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{info.description}</p>
              </div>
            </div>
            <button
              onClick={() => toggleSource(sourceId)}
              className={`w-12 h-6 rounded-full transition-colors duration-200 flex items-center ${
                sources[sourceId] 
                  ? 'bg-blue-600 justify-end' 
                  : 'bg-gray-300 dark:bg-gray-500 justify-start'
              }`}
            >
              <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-transform duration-200 ${
                sources[sourceId] ? 'translate-x-0' : 'translate-x-0'
              }`} />
            </button>
          </div>
        ))}
      </div>
      
      <div className="mt-3 text-center">
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {getEnabledSourcesCount()} su {Object.keys(sourcesInfo).length} sorgenti abilitate
        </p>
      </div>
    </div>
  );

  return (
    <>
      {/* Chat Toggle Button - Design KainoTV */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 hover:from-blue-700 hover:via-purple-700 hover:to-blue-800 text-white rounded-full p-4 shadow-xl transition-all duration-300 hover:scale-110 border border-blue-500/20 backdrop-blur-sm"
          aria-label="Apri chat sportiva KainoTV"
        >
          {isOpen ? <X size={24} /> : <MessageCircle size={24} />}
          <div className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 rounded-full animate-pulse border-2 border-white"></div>
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className={`fixed z-40 transition-all duration-300 ease-in-out ${
          isFullscreen 
            ? 'inset-0 bg-gray-900/95 backdrop-blur-sm' 
            : 'bottom-24 right-6 w-96 max-w-[calc(100vw-3rem)] h-[600px] md:h-[500px]'
        }`}>
          
          <div className={`${
            isFullscreen 
              ? 'w-full h-full flex flex-col bg-white dark:bg-gray-800' 
              : 'w-full h-full bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden'
          }`}>
            
            {/* Header - Sempre visibile con design mobile-friendly */}
            <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 text-white p-3 flex items-center justify-between flex-shrink-0">
              <div className="flex items-center space-x-2">
                <div className="relative">
                  <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                  <div className="absolute inset-0 w-3 h-3 bg-green-400 rounded-full animate-ping opacity-75"></div>
                </div>
                <div>
                  <h3 className="font-bold text-base">Assistente Sportivo</h3>
                  <p className="text-xs text-blue-100">KainoTV • Tennis & Sport</p>
                </div>
              </div>
              
              {/* Area pulsanti header */}
              <div className="flex items-center space-x-1">
                {/* Pulsante Sorgenti */}
                <button
                  onClick={() => setShowSourcesPanel(!showSourcesPanel)}
                  className={`text-white hover:text-blue-200 transition-colors p-1.5 rounded-md hover:bg-white/10 relative ${
                    getEnabledSourcesCount() > 0 ? 'bg-white/20' : ''
                  }`}
                  aria-label="Gestisci sorgenti dati"
                >
                  <Settings size={16} />
                  {getEnabledSourcesCount() > 0 && (
                    <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full text-xs flex items-center justify-center text-gray-800 font-bold">
                      {getEnabledSourcesCount()}
                    </div>
                  )}
                </button>

                {/* Toggle Fullscreen (solo su desktop) */}
                {!isFullscreen && (
                  <button
                    onClick={toggleFullscreen}
                    className="hidden md:flex text-white hover:text-blue-200 transition-colors p-1.5 rounded-md"
                    aria-label="Modalità schermo intero"
                  >
                    <Maximize2 size={16} />
                  </button>
                )}
                
                {/* Pulsante chiusura - sempre in alto a destra */}
                <button
                  onClick={closeChat}
                  className="text-white hover:text-blue-200 transition-colors p-1.5 rounded-md hover:bg-white/10"
                  aria-label="Chiudi chat"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Sources Panel */}
            {showSourcesPanel && renderSourcesPanel()}

            {/* Messages - Area scrollabile */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[85%] md:max-w-[80%] p-3 rounded-2xl shadow-sm ${
                    message.type === 'user'
                      ? 'bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-br-md'
                      : message.type === 'system'
                      ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200 rounded-bl-md border border-yellow-200 dark:border-yellow-700'
                      : 'bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-md border border-gray-200 dark:border-gray-600'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap leading-relaxed">{message.content}</p>
                    
                    {/* Mostra sorgenti utilizzate */}
                    {message.sources && message.sources.length > 0 && (
                      <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Sorgenti utilizzate:</p>
                        <div className="flex flex-wrap gap-1">
                          {message.sources.map((source, index) => (
                            <span 
                              key={index}
                              className="text-xs bg-gray-100 dark:bg-gray-600 px-2 py-1 rounded-full"
                            >
                              {source.icon} {source.name}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <p className={`text-xs mt-2 ${
                      message.type === 'user' 
                        ? 'text-blue-100' 
                        : message.type === 'system'
                        ? 'text-yellow-600 dark:text-yellow-300'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {formatTime(message.timestamp)}
                    </p>
                  </div>
                </div>
              ))}
              
              {/* Loading indicator con design KainoTV */}
              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-2xl rounded-bl-md border border-gray-200 dark:border-gray-600 p-3 flex items-center space-x-3 shadow-sm">
                    <div className="relative">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                    </div>
                    <span className="text-sm">Sto elaborando...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area - Design KainoTV con sicurezza mobile */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex-shrink-0">
              <div className="flex space-x-3 items-end">
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Chiedi informazioni su tennis, tornei, giocatori..."
                  className="flex-1 resize-none border border-gray-300 dark:border-gray-600 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400"
                  rows={1}
                  style={{
                    minHeight: '44px',
                    maxHeight: '120px'
                  }}
                  disabled={isLoading}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || isLoading}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 disabled:from-gray-300 disabled:to-gray-400 disabled:cursor-not-allowed text-white rounded-xl px-4 py-3 transition-all duration-200 flex items-center justify-center shadow-sm hover:shadow-md flex-shrink-0"
                  style={{ minWidth: '52px', height: '44px' }}
                  aria-label="Invia messaggio"
                >
                  <Send size={18} />
                </button>
              </div>
              
              {/* Footer info */}
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {getEnabledSourcesCount() > 0 ? (
                    <>� {getEnabledSourcesCount()} sorgenti attive • Powered by KainoTV</>
                  ) : (
                    <>⚠️ Nessuna sorgente attiva • Abilita sorgenti con ⚙️</>
                  )}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;
